import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  notification,
  Typography,
  Tag,
  Button,
  Spin,
  Modal,
  Input,
  Space,
  Alert,
  Tooltip,
  Tabs,
  Statistic,
  Progress,
} from "antd";
import { useParams, Link } from "react-router-dom";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import moment from "moment";
import { FinetuneRunNew, FinetuneDataset } from "../../../model_gen/finetuning";
import {
  DeploymentUnitOutlined,
  InfoCircleOutlined,
  AppstoreOutlined,
  SettingOutlined,
  CalendarOutlined,
  RocketOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import ValidationDetails from "../Validation/ValidationDetails";

const { Text } = Typography;
const { TabPane } = Tabs;

const FinetuneRunDetail: React.FC = () => {
  const { finetuneRunId } = useParams();
  const { apiKey } = usePermissions();
  const [finetuneRun, setFinetuneRun] = useState<FinetuneRunNew | null>(null);
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [subDataset, setSubDataset] = useState<FinetuneDataset | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDatasetLoading, setIsDatasetLoading] = useState<boolean>(false);
  const [isSubDatasetLoading, setIsSubDatasetLoading] =
    useState<boolean>(false);
  const [isDeployModalVisible, setIsDeployModalVisible] =
    useState<boolean>(false);
  const [isDeploying, setIsDeploying] = useState<boolean>(false);
  const [modelName, setModelName] = useState<string>("");
  const [modelNameError, setModelNameError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");

  const styles = {
    hoverRow: {
      cursor: "pointer",
      transition: "background-color 0.2s ease",
    },
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
      marginBottom: "24px",
    },
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    detailsContainer: {
      borderRadius: "12px",
      border: "1px solid #f0f0f0",
      overflow: "hidden",
      marginBottom: "24px",
    },
    detailsHeader: {
      borderBottom: "1px solid #f0f0f0",
      padding: "16px 24px",
      background: "#fafafa",
    },
    detailsContent: {
      padding: "24px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
    tabIcon: {
      marginRight: "8px",
    },
    promptContainer: {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      background: "#f9f9f9",
      padding: "16px",
      borderRadius: "8px",
      border: "1px solid #f0f0f0",
      fontSize: "14px",
      fontFamily: "monospace",
      lineHeight: "1.6",
    },
  };

  // Add global CSS for interactive elements
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .interactive-row {
        cursor: pointer;
        transition: all 0.2s ease;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-statistic-content-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
      .ant-modal-content {
        border-radius: 12px;
        overflow: hidden;
      }
      .ant-table {
        border-radius: 8px;
        overflow: hidden;
      }
      .ant-statistic-title {
        margin-bottom: 4px !important;
      }
      .ant-tag {
        border-radius: 4px;
        padding: 3px 8px;
      }
      .finetune-run-container .ant-btn:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
      }
      .finetune-run-container .ant-btn {
        transition: all 0.2s ease;
      }
      .custom-tabs .ant-tabs-tab {
        transition: all 0.3s ease;
        border-radius: 6px 6px 0 0 !important;
        padding: 8px 16px !important;
      }
      .custom-tabs .ant-tabs-tab:hover {
        color: #1890ff;
      }
      .custom-tabs .ant-tabs-tab-active {
        background-color: #f0f7ff !important;
      }
      .ant-descriptions-item-label {
        color: #8c8c8c;
        font-size: 14px;
      }
      .ant-descriptions-item-content {
        font-size: 14px;
      }
      /* Fix statistic icon alignment */
      .ant-statistic-content {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-prefix {
        display: flex;
        align-items: center;
      }
      .ant-statistic-content-value {
        display: inline-flex;
        align-items: center;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (finetuneRunId) {
      setIsLoading(true);
      FinetuneRunService.getFinetuneRunById(
        Number(finetuneRunId),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: FinetuneRunNew) => {
          setFinetuneRun(data);

          // Fetch dataset details if we have a dataset ID
          if (data.finetune_dataset_id) {
            DatasetService.getDataset(
              data.finetune_dataset_id,
              setIsDatasetLoading,
              apiKey
            ).subscribe(
              (datasetData: FinetuneDataset) => {
                setDataset(datasetData);
              },
              (error: any) => {
                console.error("Failed to fetch dataset:", error);
                notification.error({
                  message: "Error",
                  description: `Failed to fetch dataset details: ${error}`,
                });
              }
            );
          }
          if (data.meta?.sub_dataset_id) {
            DatasetService.getDataset(
              data.meta.sub_dataset_id,
              setIsSubDatasetLoading,
              apiKey
            ).subscribe(
              (datasetData: FinetuneDataset) => {
                setSubDataset(datasetData);
              },
              (error: any) => {
                console.error("Failed to fetch dataset:", error);
                notification.error({
                  message: "Error",
                  description: `Failed to fetch dataset details: ${error}`,
                });
              }
            );
          }
        },
        (error: any) => {
          console.error(error);
          notification.error({
            message: "Error",
            description: `Failed to fetch finetune run: ${error}`,
          });
          setIsLoading(false);
        }
      );
    }
  }, [finetuneRunId, apiKey]);

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      CREATED: "default",
      PENDING: "processing",
      RUNNING: "processing",
      COMPLETED: "success",
      FAILED: "error",
      CANCELLED: "warning",
    };
    return statusMap[status] || "default";
  };

  // Add this helper function to calculate estimated time remaining
  const getTimeRemaining = () => {
    if (
      finetuneRun?.status?.toUpperCase() === "RUNNING" &&
      finetuneRun?.progress &&
      finetuneRun?.date_created
    ) {
      const startTime = moment(finetuneRun.date_created);
      const now = moment();
      const elapsedMinutes = now.diff(startTime, "minutes");

      if (finetuneRun.progress > 0 && elapsedMinutes > 0) {
        // Calculate total time based on progress so far
        const totalEstimatedMinutes = elapsedMinutes / finetuneRun.progress;
        const remainingMinutes = totalEstimatedMinutes - elapsedMinutes;

        if (remainingMinutes <= 0) {
          return "Almost complete";
        }

        if (remainingMinutes < 60) {
          return `${Math.ceil(remainingMinutes)} minutes remaining`;
        } else {
          const hours = Math.floor(remainingMinutes / 60);
          const mins = Math.ceil(remainingMinutes % 60);
          return `${hours}h ${mins}m remaining`;
        }
      }
      return "Calculating...";
    }
    return null;
  };

  // Modify the getStatusColor function to handle the progress display
  const getStatusDisplay = () => {
    if (!finetuneRun) return null;

    const statusUpper = finetuneRun.status.toUpperCase();

    if (statusUpper === "RUNNING") {
      const percent = finetuneRun.progress
        ? Math.round(finetuneRun.progress * 100)
        : 0;
      return (
        <Tooltip title={getTimeRemaining()}>
          <div style={{ width: "100%" }}>
            <Progress
              percent={Number(percent.toFixed(1))}
              size="small"
              status="active"
              style={{ marginBottom: "8px" }}
            />
            <Tag
              color={getStatusColor(statusUpper)}
              style={{ marginTop: "4px" }}>
              {statusUpper}
            </Tag>
          </div>
        </Tooltip>
      );
    } else if (statusUpper === "COMPLETED") {
      return (
        <div style={{ width: "100%" }}>
          <Progress
            percent={100}
            size="small"
            status="success"
            style={{ marginBottom: "8px" }}
          />
          <Tag color={getStatusColor(statusUpper)} style={{ marginTop: "4px" }}>
            {statusUpper}
          </Tag>
        </div>
      );
    } else {
      return (
        <Tag color={getStatusColor(statusUpper)} style={styles.tagStyle}>
          {statusUpper}
        </Tag>
      );
    }
  };

  // Helper function to get display name
  const getDisplayName = () => {
    if (!finetuneRun) return "";
    return finetuneRun.display_name;
  };

  const showDeployModal = () => {
    // Get the display name and format it: replace spaces with underscores and make lowercase
    const displayName = (getDisplayName() as string) || "";
    const initialName = displayName.replace(/\s+/g, "_").toLowerCase();

    setModelName(initialName);
    validateModelName(initialName);
    setIsDeployModalVisible(true);
  };

  const handleDeployCancel = () => {
    if (isDeploying) {
      return; // Prevent closing the modal while deploying
    }
    setIsDeployModalVisible(false);
    setModelName("");
    setModelNameError(null);
  };

  const validateModelName = (name: string): boolean => {
    if (!name.trim()) {
      setModelNameError("Model name cannot be empty");
      return false;
    }

    if (name.length > 50) {
      setModelNameError("Model name cannot exceed 50 characters");
      return false;
    }

    const validNameRegex = /^[a-zA-Z0-9_\-:]+$/;
    if (!validNameRegex.test(name)) {
      setModelNameError(
        "Model name can only contain alphanumeric characters, underscores, hyphens, and colons"
      );
      return false;
    }

    setModelNameError(null);
    return true;
  };

  const handleDeploy = () => {
    if (!validateModelName(modelName)) {
      return;
    }

    setIsDeploying(true);

    FinetuneRunService.deployFinetuneRun(
      Number(finetuneRunId),
      modelName,
      setIsDeploying,
      apiKey
    ).subscribe(
      (run: FinetuneRunNew) => {
        notification.success({
          message: "Success",
          description: `Model "${modelName}" has been queued for deployment.`,
        });
        setFinetuneRun(run);
        setIsDeployModalVisible(false);
        setModelName("");
        setModelNameError(null);
        setIsDeploying(false);
      },
      (error: any) => {
        notification.error({
          message: "Error",
          description: `Failed to deploy model: ${error}`,
        });
        setIsDeploying(false);
      }
    );
  };

  // Check if the Deploy button should be shown
  const shouldShowDeployButton = () => {
    if (!finetuneRun || finetuneRun.status !== "COMPLETED") {
      return false;
    }

    const meta = finetuneRun.meta || {};
    const modelId = meta.model_id;
    const deploymentStatus = getDeploymentStatus();

    // Show deploy button if model_id is null and deployment status is null or FAILED
    return !modelId && (!deploymentStatus || deploymentStatus === "FAILED");
  };

  // Get deployment status color
  const getDeploymentStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      SCHEDULED: "processing",
      DEPLOYING: "processing",
      DEPLOYED: "success",
      FAILED: "error",
    };
    return statusMap[status] || "default";
  };

  // Helper to safely get deployment status as string
  const getDeploymentStatus = (): string | null => {
    if (!finetuneRun?.meta?.model_deployment_status) {
      return null;
    }
    return String(finetuneRun.meta.model_deployment_status);
  };

  // Check if deployment status should be displayed
  const shouldShowDeploymentStatus = (): boolean => {
    const status = getDeploymentStatus();
    return status !== null && status !== "FAILED";
  };

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  // Get the deployment status for display
  const deploymentStatus = getDeploymentStatus();
  const getIconFromStatus = (status: string) => {
    const style = {
      color: "green",
      marginRight: "8px",
      fontSize: "16px",
    };
    switch (status) {
      case "FINETUNING_SCHEDULED":
        return <ClockCircleOutlined style={style} />;
      case "RUNNING":
        return <LoadingOutlined style={style} />;
      case "COMPLETED":
        return <CheckCircleOutlined style={style} />;
      case "FAILED":
        style.color = "red";
        return <CloseCircleOutlined style={style} />;
      case "CANCELLED":
        style.color = "orange";
        return <CloseCircleOutlined style={style} />;
      case "DELETED":
        style.color = "red";
        return <DeleteOutlined style={style} />;
      case "EXPORTING":
        style.color = "blue";
        return <ExportOutlined style={style} />;
      default:
        style.color = "#8c8c8c";
        return <LoadingOutlined style={style} />;
    }
  };
  const renderOverviewTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Stats Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <div style={styles.statsCard("#1890ff")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Base Model
                </Typography.Text>
              }
              value={finetuneRun?.base_model || "Unknown"}
              prefix={
                <RocketOutlined
                  style={{
                    color: "#1890ff",
                    marginRight: "8px",
                    fontSize: "16px",
                  }}
                />
              }
              valueStyle={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#262626",
                lineHeight: "1.4",
              }}
              formatter={(value) => (
                <Tooltip title={value}>
                  <span>{value}</span>
                </Tooltip>
              )}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={styles.statsCard("#52c41a")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Date Created
                </Typography.Text>
              }
              value={moment(finetuneRun?.date_created).format("MMM D, YYYY")}
              prefix={
                <CalendarOutlined
                  style={{
                    color: "#52c41a",
                    marginRight: "8px",
                    fontSize: "16px",
                  }}
                />
              }
              valueStyle={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#262626",
                lineHeight: "1.4",
              }}
              formatter={(value) => (
                <Tooltip title={value}>
                  <span>{value}</span>
                </Tooltip>
              )}
            />
          </div>
        </Col>
        <Col xs={24} sm={8}>
          <div style={styles.statsCard("#8c8c8c")}>
            <Statistic
              title={
                <Typography.Text style={{ fontSize: "12px", color: "#8c8c8c" }}>
                  Status
                </Typography.Text>
              }
              value={finetuneRun?.status || "Unknown"}
              prefix={getIconFromStatus(finetuneRun?.status || "")}
              valueStyle={{
                fontSize: "18px",
                fontWeight: 600,
                color: "#262626",
                lineHeight: "1.4",
              }}
              formatter={() => getStatusDisplay()}
            />
          </div>
        </Col>
      </Row>

      {/* Details Section */}
      <div style={styles.detailsContainer}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Details
          </Typography.Title>
        </div>
        <div style={styles.detailsContent}>
          <Row gutter={[0, 24]}>
            <Col span={24}>
              <Typography.Text style={styles.subtitle}>
                Description
              </Typography.Text>
              <Typography.Paragraph
                style={{
                  whiteSpace: "pre-wrap",
                  margin: 0,
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}>
                {finetuneRun?.description || "No description provided"}
              </Typography.Paragraph>
            </Col>

            <Col span={12}>
              <Typography.Text style={styles.subtitle}>Dataset</Typography.Text>
              <div>
                {isDatasetLoading ? (
                  <Spin size="small" />
                ) : dataset ? (
                  <Link
                    to={`/models/datasets/${finetuneRun?.finetune_dataset_id}`}
                    style={{ color: "#1890ff", fontSize: "14px" }}>
                    {dataset.name}
                  </Link>
                ) : finetuneRun?.finetune_dataset_id ? (
                  <Link
                    to={`/models/datasets/${finetuneRun.finetune_dataset_id}`}
                    style={{ color: "#1890ff", fontSize: "14px" }}>
                    Dataset
                  </Link>
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </div>
              {finetuneRun?.meta?.sub_dataset_id && (
                <div style={{ marginTop: "8px" }}>
                  <Typography.Text style={styles.subtitle}>
                    Sub Dataset
                  </Typography.Text>
                  <div>
                    {isSubDatasetLoading ? (
                      <Spin size="small" />
                    ) : subDataset ? (
                      <Link
                        to={`/models/datasets/${finetuneRun?.meta?.sub_dataset_id}`}
                        style={{ color: "#1890ff", fontSize: "14px" }}>
                        {subDataset.name}
                      </Link>
                    ) : finetuneRun?.meta?.sub_dataset_id ? (
                      <Link
                        to={`/models/datasets/${finetuneRun.meta.sub_dataset_id}`}
                        style={{ color: "#1890ff", fontSize: "14px" }}>
                        Dataset
                      </Link>
                    ) : (
                      <Text type="secondary">-</Text>
                    )}
                  </div>
                </div>
              )}
            </Col>

            <Col span={12}>
              <Typography.Text style={styles.subtitle}>
                Data Source
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.intent_group_id ? (
                  <Link
                    to={`/intent-group/${finetuneRun.intent_group_id}`}
                    style={{ color: "#1890ff" }}>
                    {finetuneRun.intent_group_name}
                  </Link>
                ) : finetuneRun?.application_id && finetuneRun?.intent_id ? (
                  <Link
                    to={`/application/${finetuneRun.application_id}/intent/${finetuneRun.intent_id}`}
                    style={{ color: "#1890ff" }}>
                    {`${finetuneRun.application_name} / ${finetuneRun.intent_name}`}
                  </Link>
                ) : finetuneRun?.application_name &&
                  finetuneRun?.intent_name ? (
                  <Typography.Text>{`${finetuneRun.application_name} / ${finetuneRun.intent_name}`}</Typography.Text>
                ) : (
                  <Typography.Text>
                    {finetuneRun?.application_name ||
                      finetuneRun?.intent_name ||
                      finetuneRun?.intent_group_name || (
                        <Text type="secondary">-</Text>
                      )}
                  </Typography.Text>
                )}
              </div>
            </Col>

            {shouldShowDeploymentStatus() && deploymentStatus && (
              <Col span={12}>
                <Typography.Text style={styles.subtitle}>
                  Deployment Status
                </Typography.Text>
                <div>
                  <Tag
                    color={getDeploymentStatusColor(deploymentStatus)}
                    style={styles.tagStyle}>
                    {deploymentStatus}
                  </Tag>
                </div>
              </Col>
            )}
          </Row>
        </div>
      </div>

      {/* Prompt Section - if available */}
      {finetuneRun?.prompt && (
        <div style={styles.detailsContainer}>
          <div style={styles.detailsHeader}>
            <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
              Prompt
            </Typography.Title>
          </div>
          <div style={styles.detailsContent}>
            <Typography.Paragraph
              style={{
                margin: 0,
                fontSize: "14px",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                fontFamily: "monospace",
              }}>
              {finetuneRun.prompt}
            </Typography.Paragraph>
          </div>
        </div>
      )}

      {/* Prompt Deletions - if available */}
      {finetuneRun?.prompt_deletions &&
        finetuneRun.prompt_deletions.length > 0 && (
          <div style={styles.detailsContainer}>
            <div style={styles.detailsHeader}>
              <Typography.Title
                level={5}
                style={{ margin: 0, fontWeight: 600 }}>
                Prompt Deletions
              </Typography.Title>
            </div>
            <div style={styles.detailsContent}>
              <ul style={{ paddingLeft: "20px", margin: 0 }}>
                {finetuneRun.prompt_deletions.map((deletion, index) => (
                  <li
                    key={index}
                    style={{ fontSize: "14px", marginBottom: "8px" }}>
                    {deletion}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
    </div>
  );

  const renderConfigurationTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={styles.detailsContainer}>
        <div style={styles.detailsHeader}>
          <Typography.Title level={5} style={{ margin: 0, fontWeight: 600 }}>
            Configuration Settings
          </Typography.Title>
        </div>
        <div style={styles.detailsContent}>
          <Row gutter={[24, 24]}>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                Conversation Mode
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.conversation_mode ? "Yes" : "No"}
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                4-bit Quantization
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.fourbit ? "Yes" : "No"}
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                Remove Examples
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.remove_examples ? "Yes" : "No"}
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                Run Version
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.run_version || "-"}
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                Remove Example Tag
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.remove_example_tag ? "Yes" : "No"}
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                Remove Output Tag
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.remove_output_tag ? "Yes" : "No"}
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                Warmup Steps
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.meta?.warmup_steps !== undefined
                  ? String(finetuneRun.meta.warmup_steps)
                  : "-"}
              </div>
            </Col>
            <Col span={8}>
              <Typography.Text style={styles.subtitle}>
                Learning Rate
              </Typography.Text>
              <div style={{ fontSize: "14px" }}>
                {finetuneRun?.meta?.learning_rate !== undefined
                  ? String(finetuneRun.meta.learning_rate)
                  : "-"}
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );

  const renderValidationTab = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <ValidationDetails finetuneRunId={finetuneRun?.id.toString()} />
    </div>
  );

  return (
    <div
      className="finetune-run-container"
      style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Text type="secondary">
                <Link to="/models/finetuning">Finetune Runs</Link> /{" "}
                {getDisplayName()}
              </Typography.Text>
              <Typography.Title
                level={2}
                style={{ margin: 0, fontWeight: 600 }}>
                {getDisplayName()}
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                Monitor fine-tuning progress, validate results, and deploy
                models
              </Typography.Text>
            </Space>
          </Col>
          <Col>
            <Space>
              {finetuneRun && (
                <>
                  {shouldShowDeployButton() && (
                    <Button
                      type="primary"
                      icon={<DeploymentUnitOutlined />}
                      onClick={showDeployModal}>
                      Deploy
                    </Button>
                  )}
                </>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      {/* Deploy Model Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 600,
              borderBottom: "1px solid #f0f0f0",
              padding: "16px 0",
              marginBottom: "16px",
            }}>
            <DeploymentUnitOutlined />
            <span>Deploy Finetuned Model</span>
          </div>
        }
        open={isDeployModalVisible}
        onCancel={handleDeployCancel}
        footer={[
          <Button
            key="cancel"
            onClick={handleDeployCancel}
            disabled={isDeploying}>
            Cancel
          </Button>,
          <Button
            key="deploy"
            type="primary"
            onClick={handleDeploy}
            loading={isDeploying}
            disabled={
              !modelName.trim() || modelNameError !== null || isDeploying
            }>
            Deploy
          </Button>,
        ]}>
        {isDeploying ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin size="large" />
            <p style={{ marginTop: "16px" }}>Deploying model...</p>
          </div>
        ) : (
          <>
            <p>Please provide a name for your deployed model:</p>
            <Input
              placeholder="Model name"
              value={modelName}
              onChange={(e) => {
                const value = e.target.value;
                setModelName(value);
                validateModelName(value);
              }}
              maxLength={50}
              status={modelNameError ? "error" : ""}
              style={{ marginBottom: 8 }}
            />
            {modelNameError && (
              <Alert
                message={modelNameError}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
          </>
        )}
      </Modal>

      {/* Main Content with Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <AppstoreOutlined style={styles.tabIcon} />
              Overview
            </span>
          }
          key="overview">
          <div style={styles.contentCard}>{renderOverviewTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <SettingOutlined style={styles.tabIcon} />
              Configuration
            </span>
          }
          key="configuration">
          <div style={styles.contentCard}>{renderConfigurationTab()}</div>
        </TabPane>
        <TabPane
          tab={
            <span
              style={{
                padding: "0 4px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
              <ExperimentOutlined style={styles.tabIcon} />
              Results
            </span>
          }
          key="validation">
          <div style={styles.contentCard}>{renderValidationTab()}</div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default FinetuneRunDetail;
